/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo, useReducer, useState } from "react"
import { observer } from "mobx-react-lite"
import {
  Alert,
  AlertButton,
  Appearance,
  Platform,
  SectionListData,
  SectionListRenderItemInfo,
  ViewStyle,
} from "react-native"
import {
  Button,
  FormControl,
  Screen,
  SettingItem,
  SettingItemProps,
  Text,
  useAutoFeederPond,
} from "../../../../components"
import { useStores } from "../../../../models"
import { CompositeScreenProps } from "@react-navigation/native"
import { color, nativeBaseTheme } from "../../../../theme"
import { Divider, ScrollView, SectionList, Toast, View } from "native-base"
import { goBack } from "../../../../navigators"
import { translate } from "../../../../i18n"
import _ from "lodash"
import numeral from "numeral"
import { SafeAreaView } from "react-native-safe-area-context"
import { AutoFeederPond } from "../../../../database"
import moment from "moment/moment"
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  flex: 1,
}

const SECTION_LIST_CONTENT_CONTAINER: ViewStyle = {
  flexGrow: 1
}

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const BUTTON_CONTAINER: ViewStyle = {
  paddingBottom: 16,
  paddingHorizontal: 16,
}

// const DELETE_BUTTON: ViewStyle = {
//   borderColor: color.palette.red500,
//   width: "30%",
//   margin: 16
// }

const SectionHeader: FC<{ colorScheme?: "light" | "dark", info: { section: SectionListData<any, any> } }> = observer(function SectionHeader(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info: { section } } = props;

  return (
    <View
      borderTopWidth={section.index !== 0 && "4px" || 0}
      borderColor={`gray.${colorScheme === 'light' ? 200 : 800}`}
    >
      {section.title && <Text text={section.title} preset="title4" marginX="8px" marginY="4px" colorScheme={colorScheme} />}
    </View>
  )
})

const Item: FC<{ colorScheme?: "light" | "dark", info: SectionListRenderItemInfo<any, any> }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info: { item } } = props;

  return (
    <SettingItem
      type={item.type}
      colorScheme={colorScheme}
      actionsheetProps={{ title: item.label }}
      value={item.value}
      inputProps={item.inputProps}
      onValueChange={item.onValueChange}
      onPress={item.onPress}
      onPressIn={item.onPressIn}
      onPressOut={item.onPressOut}
      isDisabled={item.isDisabled}
      renderActionSheetComponent={item.renderActionSheetComponent}
    >
      <View padding="8px">
        <Text
          text={item.label}
          preset="body1"
          colorScheme={colorScheme}
          color={(item.isDisabled && nativeBaseTheme.colors.muted["500"]) || item.labelColor}
          marginBottom="4px"
        />
        {!!(item.valueFormatted || item.value) && (
          <Text
            text={item.valueFormatted || item.value}
            preset="body2"
            color={(item.isDisabled && nativeBaseTheme.colors.muted["500"]) || item.labelColor || nativeBaseTheme.colors.dark[colorScheme === "light" ? "400" : "600"]}
            colorScheme={colorScheme}
          />
        )}
      </View>
    </SettingItem>
  )
})

const ActionSheetComponentWifi: FC<SettingItemProps> = observer(function ActionSheetComponentWifi(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    formControlProps: {
      inputProps: { defaultValue, ...formControlInputProps } = {},
      ...restFormControlProps
    } = {}, inputProps,
    onValueChange
  } = props

  const [ state, dispatch ] = useReducer((prevState, action) => {
    switch (action.type) {
      case 'set-ssid':
        prevState.ssid = action.payload
        break;
      case 'set-password':
        prevState.password = action.payload;
        break;
    }
    return { ...prevState };
  }, defaultValue || {})

  const onChangeSsid = useCallback((value: string) => {
    dispatch({ type: 'set-ssid', payload: value })
  }, [])

  const onChangePassword = useCallback((value: string) => {
    dispatch({ type: 'set-password', payload: value })
  }, [])

  const onPress = useCallback(() => onValueChange(state), [onValueChange, state])

  return (
    <ScrollView>
      <FormControl
        colorScheme={colorScheme}
        labelTx="autoFeederPondEditScreen.ssid"
        inputProps={{
          autoCapitalize: "none",
          autoComplete: "off",
          autoCorrect: false,
          textContentType: "none",
          keyboardType: "default",
          returnKeyType: "go",
          autoFocus: true,
          defaultValue: state.ssid,
          onChangeText: onChangeSsid,
          onSubmitEditing: undefined,
          ...formControlInputProps,
          ...inputProps
        }}
        style={FORM_CONTROL}
        {...restFormControlProps}
      />
      <FormControl
        colorScheme={colorScheme}
        labelTx="autoFeederPondEditScreen.password"
        inputProps={{
          type: "password",
          autoCapitalize: "none",
          autoComplete: "password",
          autoCorrect: false,
          textContentType: "password",
          returnKeyType: "go",
          defaultValue: state.password,
          onChangeText: onChangePassword,
          onSubmitEditing: onPress,
          ...formControlInputProps,
          ...inputProps
        }}
        style={FORM_CONTROL}
        {...restFormControlProps}
      />
      <View style={BUTTON_CONTAINER}>
        <Button
          testID="connect-button"
          tx="connect"
          colorScheme={colorScheme}
          onPress={onPress}
        />
      </View>
    </ScrollView>
  );
})

export const AutoFeederPondEditScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function AutoFeederPondEditScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { autoFeederPond: model, setAutoFeederPond: setModel } = useAutoFeederPond()
  const originalState = model && model.getAttributes().state;
  const state = useMemo(() => (
    originalState ? model.getAttribute('state') : undefined
  ), [model, originalState])

  // Pull in one of our MST stores
  const {
    appStore: { farmStore: { pondStore: { autoFeederPond } } },
    autoFeederPondStore: modelStore, autoFeederPondStore: { loading }
  } = useStores()

  const [ wifiConnecting, setWifiConnecting ] = useState<boolean>(false)

  const connect = useCallback(async (value: "bluetooth"|"wifi" = "bluetooth") => {
    if (!model) return;
    modelStore.set('loading', true);
    await model.connect(value).catch(() => {
      Toast.show({ title: translate('autoFeederPondDetailScreen.failedToConnect') })
      return null
    })
    if (model?.isConnected) await model.commandSetDateTime()
    setModel(model)
    modelStore.set('loading', false);
  }, [model, modelStore, setModel])

  const disconnect = useCallback(async () => {
    if (!model) return;
    modelStore.set('loading', true);
    await model.disconnect().catch()
    setModel(model)
    modelStore.set('loading', false);
    JalaAnalytic.log('Mobile App : Asto - Setting disconnect');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, modelStore])

  useEffect(() => {
    modelStore.init()
  }, [modelStore])

  useEffect(() => {
    if (!model) setModel(AutoFeederPond.hydrate({ ...autoFeederPond }))
  }, [autoFeederPond, model, setModel])

  const onWifiValueChange = useCallback(async (value) => {
    model.ip = null
    setWifiConnecting(true)
    try {
      model.ip = await model.writeUntilResponse(`;12;${value.ssid};${value.password}#`, 15000);
    } catch (e) {
      //
    } finally {
      setWifiConnecting(false)
    }
    await modelStore.updateOffline(model, { state: {
      ...state,
      updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
      ip: model.ip,
      ssidSetting: value || null
    } })
    setModel(model)
    if (!model.ip) Toast.show({ title: translate('autoFeederPondEditScreen.failedToConnect') })
  }, [model, modelStore, setModel, state])

  const onPressIn = useCallback(async () => {
    JalaAnalytic.log('Mobile App : Asto - Setting run test');
    await model.commandTestRun(1)
  }, [model])

  const runTestOnTime = useCallback(async () => {
    await model.commandTestRun(state?.onTime || 1)
  }, [model, state?.onTime])

  const clearSetting = useCallback(async () => {
    const clearButton: AlertButton = {
      text: 'Clear',
      onPress: async () => {
        await model.commandClear()
      },
      style: "destructive"
    }
    JalaAnalytic.log('Mobile App : Asto - Setting clear');
    Alert.alert('Are you sure?', undefined, [
      { text: 'Cancel', style: "cancel" },
      Platform.OS !== 'web' ? undefined : clearButton,
      Platform.OS === 'web' ? undefined : clearButton,
    ])
  }, [model])

  const handleDelete = useCallback(async () => {
    const buttonDelete: AlertButton = {
      text: 'Delete',
      onPress: async () => {
        modelStore.set('loading', true)
        const success = await modelStore.delete(model)
        if (success) {
          goBack('Home.Stack')
          goBack('Home.Stack')
        }
        modelStore.set('loading', false)
      },
      style: "destructive"
    }
    JalaAnalytic.log('Mobile App : Asto - Setting delete');
    Alert.alert('Are you sure?', undefined, [
      { text: 'Cancel', style: "cancel" },
      Platform.OS !== 'web' ? undefined : buttonDelete,
      Platform.OS === 'web' ? undefined : buttonDelete,
    ])
  }, [model, modelStore])

  const renderActionSheetComponentWifi = useCallback((props: SettingItemProps) => {
    const { value: valueProps, formControlProps: { inputProps, ...formControlProps } = {}, ...restProps } = props;
    const value = valueProps && state?.ssidSetting || state?.ssidSetting;

    return (
      <ActionSheetComponentWifi
        {...restProps}
        formControlProps={{
          ...formControlProps,
          inputProps: {
            ...inputProps,
            defaultValue: value
          }
        }}
      />
    )
  }, [state])

  const isConnected = useMemo(() => !!model?.isConnected, [model?.isConnected])

  const sections = useMemo(() => (!model && []) || [
    {
      title: translate('autoFeederPondEditScreen.connection'),
      data: [
        {
          type: 'action',
          label: translate('autoFeederPondEditScreen.bluetooth'),
          valueColor: isConnected ? color.palette.green700 : color.palette.red700,
          value: (loading && translate('autoFeederPondEditScreen.connecting')) ||
            (isConnected && translate('autoFeederPondEditScreen.connected')) ||
            `${translate('autoFeederPondEditScreen.disconnected')}. ${translate('autoFeederPondEditScreen.tapToReconnect')}`,
          onPress: () => {
            JalaAnalytic.log('Mobile App : Asto - Setting connection');
            if (loading) return;
            if (!isConnected) {
              connect().catch();
            } else {
              disconnect().catch()
            }
          }
        },
        {
          isDisabled: !isConnected,
          type: 'custom',
          label: translate('autoFeederPondEditScreen.wifi'),
          valueColor: isConnected ? color.palette.green700 : color.palette.red700,
          value: (wifiConnecting && translate('autoFeederPondEditScreen.connecting')) ||
            state?.ip || translate('autoFeederPondEditScreen.tapToConnect'),
          renderActionSheetComponent: renderActionSheetComponentWifi,
          onValueChange: onWifiValueChange
        }
      ] as Array<SettingItemProps>
    },
    {
      title: translate('autoFeederPondEditScreen.general'),
      data: [
        {
          type: 'picker',
          label: translate('autoFeederPondEditScreen.mode'),
          value: state?.mode || 'normal',
          get valueFormatted() {
            return _.upperFirst(this.value)
          },
          inputProps: {
            data: [
              { id: 'normal', name: translate('autoFeederPondEditScreen.normal') },
              { id: 'continuous', name: translate('autoFeederPondEditScreen.continuous') },
            ]
          },
          onValueChange: async (value) => {
            await modelStore.updateOffline(model, { state: {
              ...state,
              updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
              mode: value || "normal"
            } })
            setModel(model)
            JalaAnalytic.log('Mobile App : Asto - Setting mode', { mode: value });
          }
        },
        {
          type: 'numeral-input',
          label: translate('autoFeederPondEditScreen.throwDebit'),
          value: model.throwDebit,
          valueFormatted: numeral(model.throwDebit).format('0,0.[00]'),
          // unit: translate('autoFeederPondCreateWizardScreen.debitUnit'),
          onValueChange: async (value) => {
            model.throwDebit = value
            await modelStore.updateOffline(model, {})
            setModel(model)
            JalaAnalytic.log('Mobile App : Asto - Setting feed debit', { debit: value });
          }
        }
      ] as Array<SettingItemProps>
    },
    state?.mode === 'normal' ? {
      title: translate('autoFeederPondEditScreen.normal'),
      data: [
        {
          type: 'numeral-input',
          label: translate('autoFeederPondEditScreen.timesPerDay'),
          value: state?.timesPerDay,
          valueFormatted: numeral(state?.timesPerDay).format('0,0'),
          onValueChange: async (value) => {
            const v = (value > 8) ? 8 : value;
            await modelStore.updateOffline(model, {
              state: {
                ...state,
                updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
                timesPerDay: (v > 0 && v) || 1,
                feedsPerDay: []
              },
            })
            setModel(model)
            JalaAnalytic.log('Mobile App : Asto - Setting total feed', { timesPerDat: (v > 0 && v) || 1 });
          }
        },
        // {
        //   type: 'numeral-input',
        //   label: translate('autoFeederPondEditScreen.timesPerPeriod'),
        //   value: state?.timesPerPeriod,
        //   valueFormatted: numeral(state?.timesPerPeriod).format('0,0'),
        //   onValueChange: async (value) => {
        //     const v = (value > 60) ? 60 : value;
        //     await modelStore.updateOffline(model, { state: {
        //       ...state,
        //       updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        //       timesPerPeriod: (v > 0 && v) || 1
        //     } })
        //     setModel(model)
        //   }
        // },
        {
          type: 'numeral-input',
          label: translate('autoFeederPondEditScreen.onTime'),
          value: state?.onTime || null,
          valueFormatted: state?.onTime ? numeral(state?.onTime).format('0,0.[00]') : '-',
          // unit: translate("autoFeederPondCreateWizardScreen.onTimeUnit"),
          onValueChange: async (value) => {
            await modelStore.updateOffline(model, { state: {
              ...state,
              updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
              onTime: value || 1
            } })
            setModel(model)
            JalaAnalytic.log('Mobile App : Asto - Setting timer on', { onTime: value || 1 });
          }
        },
        {
          type: 'numeral-input',
          label: translate('autoFeederPondEditScreen.intervalPeriod'),
          value: state?.intervalPeriod || null,
          valueFormatted: state?.intervalPeriod ? numeral(state?.intervalPeriod).format('0,0.[00]') : '-',
          onValueChange: async (value) => {
            const v = (value > 240) ? 240 : (value < 30 ? 30 : value);
            await modelStore.updateOffline(model, { state: {
              ...state,
              updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
              intervalPeriod: (v > 0 && v) || 1
            } })
            setModel(model)
            JalaAnalytic.log('Mobile App : Asto - Setting interval', { interval: (v > 0 && v) || 1 });
          }
        },
      ] as Array<SettingItemProps>
    } : {
      title: translate('autoFeederPondEditScreen.continuous'),
      data: [
        // {
        //   type: 'picker',
        //   label: translate('autoFeederPondCreateWizardScreen.throwingDistance'),
        //   value: state?.mode || 'normal',
        //   get valueFormatted() {
        //     return _.upperFirst(this.value)
        //   },
        //   inputProps: {
        //     data: [
        //       { id: "short", name: translate('autoFeederPondCreateWizardScreen.short') },
        //       { id: "medium", name: translate('autoFeederPondCreateWizardScreen.medium') },
        //       { id: "long", name: translate('autoFeederPondCreateWizardScreen.long') },
        //       { id: "manual", name: translate('autoFeederPondCreateWizardScreen.manual') },
        //     ]
        //   },
        //   onValueChange: async (value) => {
        //     await modelStore.updateOffline(model, { state: {
        //       ...state,
        //       updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        //       mode: value || "normal"
        //     } })
        //     setModel(model)
        //     JalaAnalytic.log('Mobile App : Asto - Setting mode', { mode: value });
        //   }
        // },
        // {
        //   type: 'numeral-input',
        //   label: translate('autoFeederPondCreateWizardScreen.duration'),
        //   value: state?.onTime,
        //   valueFormatted: numeral(state?.onTime).format('0,0.[00]'),
        //   onValueChange: async (value) => {
        //     await modelStore.updateOffline(model, { state: {
        //       ...state,
        //       updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        //       onTime: value || 1
        //     } })
        //     setModel(model)
        //     JalaAnalytic.log('Mobile App : Asto - Setting timer on continuos', { onTime: value || 1 });
        //   }
        // },
        {
          type: 'time-picker',
          label: translate('autoFeederPondEditScreen.startTime'),
          value: state?.startTime,
          valueFormatted: state?.startTime || '-',
          onValueChange: async (value) => {
            await modelStore.updateOffline(model, { state: {
              ...state,
              updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
              startTime: value || '00:00:00'
            } })
            setModel(model)
            JalaAnalytic.log('Mobile App : Asto - Setting start time', { start: value || '00:00:00' });
          }
        },
        {
          type: 'time-picker',
          label: translate('autoFeederPondEditScreen.endTime'),
          value: state?.endTime,
          valueFormatted: state?.endTime || '-',
          onValueChange: async (value) => {
            await modelStore.updateOffline(model, { state: {
              ...state,
              updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
              endTime: value || '23:59:59'
            } })
            setModel(model)
            JalaAnalytic.log('Mobile App : Asto - Setting end time', { end: value || '23:59:59' });
          }
        },
        {
          type: 'numeral-input',
          label: translate('autoFeederPondEditScreen.onTime'),
          value: state?.onTime,
          valueFormatted: numeral(state?.onTime).format('0,0.[00]'),
          onValueChange: async (value) => {
            await modelStore.updateOffline(model, { state: {
              ...state,
              updatedAt: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
              onTime: value || 1
            } })
            setModel(model)
            JalaAnalytic.log('Mobile App : Asto - Setting timer on continuos', { onTime: value || 1 });
          }
        }
      ] as Array<SettingItemProps>
    },
    {
      title: translate('autoFeederPondEditScreen.misc'),
      data: [
        {
          isDisabled: !isConnected,
          type: 'action',
          label: translate('autoFeederPondEditScreen.testRun'),
          onPress: onPressIn,
          labelColor: color.palette.blueJalaBase
        },
        {
          isDisabled: !isConnected,
          type: 'action',
          label: `${translate('autoFeederPondEditScreen.testRun')}: ${state?.onTime ? numeral(state.onTime).format('0,0.[00]') : '-'} ${translate('autoFeederPondEditScreen.onTimeUnit')}`,
          onPress: runTestOnTime,
          labelColor: color.palette.blueJalaBase
        },
        {
          isDisabled: !isConnected,
          type: 'action',
          label: translate('autoFeederPondEditScreen.clearSetting'),
          onPress: clearSetting,
          labelColor: color.palette.blueJalaBase
        },
        {
          isDisabled: !isConnected,
          type: 'action',
          label: translate('autoFeederPondEditScreen.disconnect'),
          onPress: disconnect,
          labelColor: color.palette.blueJalaBase
        },
        {
          isDisabled: loading,
          type: 'action',
          label: translate('autoFeederPondEditScreen.delete'),
          labelColor: nativeBaseTheme.colors.danger['500'],
          onPress: handleDelete
        }
      ].filter((d) => d) as Array<SettingItemProps>
    }
  ], [model, isConnected, loading, wifiConnecting, state, renderActionSheetComponentWifi, onWifiValueChange, onPressIn, runTestOnTime, clearSetting, disconnect, handleDelete, connect, modelStore, setModel])

  const renderSectionHeader = useCallback((info) => (
    <SectionHeader colorScheme={colorScheme} info={info} />
  ), [colorScheme])

  // const renderSectionFooter = useCallback(() => (
  //   <>
  //     <Divider height={1} backgroundColor={color.palette.gray300} />
  //     <Button
  //       text={translate('autoFeederPondEditScreen.delete')}
  //       preset="outline"
  //       style={DELETE_BUTTON}
  //       textColor={color.palette.red500}
  //       onPress={handleDelete}
  //       disabled={loading}
  //     />
  //     <SafeAreaView edges={['bottom']} />
  //   </>
  // ), [handleDelete, loading])

  const renderItem = useCallback((info) => (
    <Item colorScheme={colorScheme} info={info} />
  ), [colorScheme])

  const keyExtractor = useCallback((item, index) => (`${item.label}-${index}`), [])

  if (!model) return null;

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === 'light' ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
    >
      <SectionList
        bounces={false}
        sections={sections}
        renderSectionHeader={renderSectionHeader}
        renderItem={renderItem}
        contentContainerStyle={SECTION_LIST_CONTENT_CONTAINER}
        keyExtractor={keyExtractor}
        keyboardShouldPersistTaps="handled"
        // SectionSeparatorComponent={Divider}
        ItemSeparatorComponent={Divider}
        ListHeaderComponent={<View paddingX="8px" paddingY="16px">
          <Text preset="display3" text={model && (model.name || model.auto_feeder_address) || '-'} colorScheme={colorScheme} />
          <Text preset="body3" text={model && (model.name && model.auto_feeder_address) || '-'} colorScheme={colorScheme} />
        </View>}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
      />
    </Screen>
  )
})
